import {
  Route,
  Outlet,
  Routes,
  Navigate,
  BrowserRouter,
} from 'react-router-dom';

import { ERoleAccess } from '@/enum';
import AppLayout from '@/layout/appLayout';
import AuthLayout from '@/layout/authLayout';
import Admin from '@/pages/admin';
import CheckYourEmail from '@/pages/auth/CheckYourEmail';
import ForgotPassword from '@/pages/auth/ForgotPassword';
import Login from '@/pages/auth/Login';
import PasswordSuccess from '@/pages/auth/PasswordSuccess';
import ResetPassword from '@/pages/auth/ResetPassword';
import ChildrenPage from '@/pages/children';
import ContentManagement from '@/pages/content-management';
import Dashboard from '@/pages/dashboard';
import DeviceManagement from '@/pages/deviceManagement';
import EditorialPage from '@/pages/editorial';
import AddEditPolicy from '@/pages/editorial/componenets/AddEditPolicy';
import ViewPolicy from '@/pages/editorial/componenets/ViewPolicy';
import MaintenancePage from '@/pages/maintenance';
import Notifications from '@/pages/notifications';
import Parent from '@/pages/parent';
import ReportAnalytics from '@/pages/report-analytics';
import School from '@/pages/school';
import StudentList from '@/pages/school/StudentList';
import TeacherList from '@/pages/school/TeacherList';
import Settings from '@/pages/settings';
import SupportQueries from '@/pages/support';
import SupportDetails from '@/pages/support/SupportDetails';
import Teacher from '@/pages/teacher';
import TeacherLessonList from '@/pages/teacher/TeacherLessonList';

import MaintenanceGuard from './MaintenanceGuard';
import { PublicRoute, PrivateRoute, SectionGuard } from './RouteGuard';

const AppRoutes = () => {
  return (
    <BrowserRouter>
      <MaintenanceGuard>
        <Routes>
          <Route path="/maintenance" element={<MaintenancePage />} />
          <Route
            element={
              <PublicRoute>
                <AuthLayout>
                  <Outlet />
                </AuthLayout>
              </PublicRoute>
            }
          >
            <Route path="/login" element={<Login />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/check-email" element={<CheckYourEmail />} />
            <Route path="/success" element={<PasswordSuccess />} />
            {/* Fallback for public routes */}
            <Route path="*" element={<Navigate to="/login" replace />} />
          </Route>

          <Route
            element={
              <PrivateRoute>
                <AppLayout>
                  <Outlet />
                </AppLayout>
              </PrivateRoute>
            }
          >
            <Route path="/" element={<Dashboard />} />
            <Route
              path="/user-management/children/:id"
              element={
                <SectionGuard section={ERoleAccess.USER_MANAGEMENT}>
                  <ChildrenPage />
                </SectionGuard>
              }
            />
            <Route
              path="/user-management/teacher"
              element={
                <SectionGuard section={ERoleAccess.USER_MANAGEMENT}>
                  <Teacher />
                </SectionGuard>
              }
            />
            <Route
              path="/user-management/teacher/:id/lesson-list"
              element={
                <SectionGuard section={ERoleAccess.USER_MANAGEMENT}>
                  <TeacherLessonList />
                </SectionGuard>
              }
            />
            <Route
              path="/user-management/school"
              element={
                <SectionGuard section={ERoleAccess.USER_MANAGEMENT}>
                  <School />
                </SectionGuard>
              }
            />
            <Route
              path="/user-management/school/:id/teacher"
              element={
                <SectionGuard section={ERoleAccess.USER_MANAGEMENT}>
                  <TeacherList />
                </SectionGuard>
              }
            />
            <Route
              path="/user-management/school/:id/student"
              element={
                <SectionGuard section={ERoleAccess.USER_MANAGEMENT}>
                  <StudentList />
                </SectionGuard>
              }
            />
            <Route
              path="/user-management/admin"
              element={
                <SectionGuard section={ERoleAccess.USER_MANAGEMENT}>
                  <Admin />
                </SectionGuard>
              }
            />

            <Route
              path="/user-management/parents"
              element={
                <SectionGuard section={ERoleAccess.USER_MANAGEMENT}>
                  <Parent />
                </SectionGuard>
              }
            />
            <Route
              path="/editorial-management"
              element={
                <SectionGuard section={ERoleAccess.CONTENT_MANAGEMENT}>
                  <EditorialPage />
                </SectionGuard>
              }
            />
            <Route
              path="/editorial-management/policy/add"
              element={
                <SectionGuard section={ERoleAccess.CONTENT_MANAGEMENT}>
                  <AddEditPolicy />
                </SectionGuard>
              }
            />
            <Route
              path="/editorial-management/policy/edit/:id"
              element={
                <SectionGuard section={ERoleAccess.CONTENT_MANAGEMENT}>
                  <AddEditPolicy />
                </SectionGuard>
              }
            />
            <Route
              path="/editorial-management/policy/view/:id"
              element={
                <SectionGuard section={ERoleAccess.CONTENT_MANAGEMENT}>
                  <ViewPolicy />
                </SectionGuard>
              }
            />
            <Route path="/settings" element={<Settings />} />
            <Route
              path="/support-queries"
              element={
                <SectionGuard section={ERoleAccess.SUPPORT_QUERIES}>
                  <SupportQueries />
                </SectionGuard>
              }
            />
            <Route
              path="/support-queries/:id"
              element={
                <SectionGuard section={ERoleAccess.SUPPORT_QUERIES}>
                  <SupportDetails />
                </SectionGuard>
              }
            />

            <Route
              path="/report-analytics/:id"
              element={
                <SectionGuard section={ERoleAccess.REPORTS_ANALYTICS}>
                  <ReportAnalytics />
                </SectionGuard>
              }
            />
            <Route
              path="/device-management"
              element={
                <SectionGuard section={ERoleAccess.DEVICE_MANAGEMENT}>
                  <DeviceManagement />
                </SectionGuard>
              }
            />
            <Route
              path="/notifications"
              element={
                <SectionGuard section={ERoleAccess.NOTIFICATIONS}>
                  <Notifications />
                </SectionGuard>
              }
            />

            <Route
              path="/content-management"
              element={
                <SectionGuard section={ERoleAccess.CONTENT_MANAGEMENT}>
                  <ContentManagement />
                </SectionGuard>
              }
            />

            {/* Fallback for private routes */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Route>
        </Routes>
      </MaintenanceGuard>
    </BrowserRouter>
  );
};

export default AppRoutes;
